// The editor chrome of the sheet pagination (design doc §8): the backdrop
// band drawn over every sheet gap `paginate.ts` planned, with the page
// header/footer strips repeated around it (the real ones stay in the
// document — the header at the top of sheet 1, the footer at its end) and,
// in the template editor, the sheet counter.
//
// Plain DOM, not React: this hangs off the editor, not off a component. And
// deliberately NEXT TO the paper rather than inside it — the paper IS the
// ProseMirror root, and ProseMirror both parses foreign children back into
// the document and drops them again on its next redraw (see the regression
// test). The overlay is laid over the paper instead, so nothing the pass
// draws is ever part of the document.

import type { SheetGeometry } from "./paginate"

const OVERLAY_CLASS = "sheet-bands"
const BAND_CLASS = "sheet-band"

export interface SheetBandOptions {
  /** The live `pageHeader` strip NodeView, cloned below every band. */
  header?: HTMLElement | null
  /** The live `pageFooter` strip NodeView, cloned above every band. */
  footer?: HTMLElement | null
  /** Server-rendered strip markup for a paper that has NO live strip nodes —
   *  the conformity block editor, whose tree never carries the strips
   *  (`build_document_blocks` drops them) although the PDF prints at least
   *  the legacy footer on every page (design §8, review I3). Trusted HTML
   *  from the same renderer the PDF uses (`ConformityBlocks.pageHeaderHtml` /
   *  `pageFooterHtml`), inert and `aria-hidden` like every other clone.
   *  Ignored for the side a live strip already covers. */
  headerHtml?: string | null
  footerHtml?: string | null
  /** Show "2 / 5" on the band (template editor). */
  counter?: boolean
}

/** Replaces the paper's sheet bands with one per boundary (`boundariesPx` =
 *  each band's top edge, px below the paper's top edge), in an overlay
 *  covering the paper exactly — so a band's placement is the boundary the
 *  planner computed, and `left: 0; right: 0` spans the whole sheet. */
export function renderBands(
  paper: HTMLElement,
  boundariesPx: number[],
  geometry: SheetGeometry,
  {
    header,
    footer,
    headerHtml,
    footerHtml,
    counter = false,
  }: SheetBandOptions = {},
): void {
  const host = paper.parentElement
  if (!host) return
  const existing = Array.from(host.children).find((el) =>
    el.classList.contains(OVERLAY_CLASS),
  )
  // A live strip is a real document child and already prints on sheet 1 /
  // the last sheet; server markup is not, so the overlay draws those two
  // edges as well — which it must do even on a single-sheet document, where
  // there is no boundary at all.
  const staticHeader = header ? null : (headerHtml ?? null)
  const staticFooter = footer ? null : (footerHtml ?? null)
  if (!boundariesPx.length && !staticHeader && !staticFooter) {
    existing?.remove()
    return
  }
  // Everything the bands are drawn from. A pass that changes none of it
  // rewrites nothing, so the pass can safely re-run itself to a fixed point.
  const signature = [
    boundariesPx.map(Math.round).join("|"),
    Math.round(geometry.gapPx),
    counter ? "counter" : "",
    paper.offsetTop,
    paper.offsetLeft,
    paper.offsetWidth,
    header?.innerHTML ?? staticHeader ?? "",
    footer?.innerHTML ?? staticFooter ?? "",
  ].join("~")
  if (existing instanceof HTMLElement) {
    if (existing.dataset.sheetSignature === signature) return
  }
  const overlay = (existing as HTMLElement | undefined) ?? newOverlay()
  overlay.dataset.sheetSignature = signature
  if (!existing) {
    // The overlay's containing block — and the paper's offset parent, so
    // this has to be settled before the offsets below are read. Left alone
    // when the host already positions itself (an empty value is a
    // detached/not-yet-styled host).
    const position = getComputedStyle(host).position
    if (!position || position === "static") host.style.position = "relative"
  }
  overlay.replaceChildren()
  overlay.style.top = `${paper.offsetTop}px`
  overlay.style.left = `${paper.offsetLeft}px`
  overlay.style.width = `${paper.offsetWidth}px`
  if (!existing) host.append(overlay)

  const sheets = boundariesPx.length + 1
  boundariesPx.forEach((boundaryPx, index) => {
    const band = document.createElement("div")
    band.className = BAND_CLASS
    band.style.top = `${Math.round(boundaryPx)}px`
    band.style.height = `${Math.round(geometry.gapPx)}px`
    // A band sits between sheet `index + 1` and sheet `index + 2`: the footer
    // above it belongs to the first, the header below it to the second.
    const footerClone =
      stripClone(footer, "is-footer") ??
      htmlClone(staticFooter, "is-footer", { sheet: index + 1, sheets })
    if (footerClone) band.append(footerClone)
    const headerClone =
      stripClone(header, "is-header") ??
      htmlClone(staticHeader, "is-header", { sheet: index + 2, sheets })
    if (headerClone) band.append(headerClone)
    if (counter) {
      const label = document.createElement("span")
      label.className = "sheet-counter"
      label.textContent = `${index + 1} / ${sheets}`
      band.append(label)
    }
    overlay.append(band)
  })

  // The two edges a band cannot cover: the top of sheet 1 and the bottom of
  // the last sheet. Zero-height markers, so the clones position against them
  // with the very same `.is-header`/`.is-footer` rules the bands use and land
  // in the page margin the geometry reserved.
  const edge = (topPx: number, clone: HTMLElement): void => {
    const marker = document.createElement("div")
    marker.className = "sheet-strip-edge"
    marker.style.top = `${Math.round(topPx)}px`
    marker.append(clone)
    overlay.append(marker)
  }
  const headerEdge = htmlClone(staticHeader, "is-header", { sheet: 1, sheets })
  if (headerEdge) edge(0, headerEdge)
  const footerEdge = htmlClone(staticFooter, "is-footer", {
    sheet: sheets,
    sheets,
  })
  if (footerEdge) {
    const stride = geometry.pageHeightPx + geometry.gapPx
    edge((sheets - 1) * stride + geometry.pageHeightPx, footerEdge)
  }
}

function newOverlay(): HTMLElement {
  const overlay = document.createElement("div")
  overlay.className = OVERLAY_CLASS
  overlay.setAttribute("aria-hidden", "true")
  overlay.style.position = "absolute"
  // Purely decorative: every click, drag and caret goes to the paper below.
  overlay.style.pointerEvents = "none"
  return overlay
}

/** A dead copy of a strip's paragraphs — its editor chrome (the NodeView's
 *  label chip, the dashed frame) stays behind, and every `data-*`/`id` is
 *  stripped so the clone carries no node identity and no duplicate id. */
function stripClone(
  strip: HTMLElement | null | undefined,
  variant: "is-header" | "is-footer",
): HTMLElement | null {
  if (!strip) return null
  const source =
    strip.querySelector<HTMLElement>("[data-node-view-content]") ?? strip
  const clone = source.cloneNode(true) as HTMLElement
  for (const el of [clone, ...Array.from(clone.querySelectorAll("*"))]) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith("data-") || attr.name === "id") {
        el.removeAttribute(attr.name)
      }
    }
  }
  clone.className = `sheet-strip-clone ${variant}`
  clone.setAttribute("contenteditable", "false")
  clone.setAttribute("aria-hidden", "true")
  return clone
}

/** The same dead copy, built from the renderer's own strip markup instead of
 *  a live NodeView (review I3). The HTML comes from the backend that renders
 *  the PDF — the one place this app trusts markup — and lands in an overlay
 *  that is inert, `pointer-events: none` and outside the ProseMirror root. */
function htmlClone(
  html: string | null | undefined,
  variant: "is-header" | "is-footer",
  page: { sheet: number; sheets: number },
): HTMLElement | null {
  if (!html) return null
  const clone = document.createElement("div")
  clone.innerHTML = html
  // Chromium fills these in the PDF (they are what `page.current`/`page.total`
  // and the legacy footer render to); on screen nothing would, so the estimate
  // would show the footer with its page numbers missing.
  for (const el of clone.querySelectorAll(".pageNumber")) {
    el.textContent = String(page.sheet)
  }
  for (const el of clone.querySelectorAll(".totalPages")) {
    el.textContent = String(page.sheets)
  }
  clone.className = `sheet-strip-clone ${variant}`
  clone.setAttribute("contenteditable", "false")
  clone.setAttribute("aria-hidden", "true")
  return clone
}
